import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/OnboardingWizard/quizStepsConfig";
import type { GoalResultVariant } from "./types";

const I18n = I18nUtil.createScoped(Translations);

export type QuizQuestionKind = "name" | "choice" | "numeric";

export interface QuizStepDefinition {
  stepId:
    | "quiz/name"
    | "quiz/age"
    | "quiz/income"
    | "quiz/expenses"
    | "quiz/cash"
    | "quiz/debt"
    | "quiz/invest"
    | "quiz/risk";
  figmaNodeIds: string[];
  kind: QuizQuestionKind;
  question: string;
  subtitle?: string;
  /** Ed bubble copy for numeric steps (Figma per-question message). */
  heroMessage?: string | ((displayName: string) => string);
  options?: { id: string; label: string }[];
  numeric?: {
    min: number;
    max: number;
    step: number;
    defaultValue: number;
    /** When true, min (typically 0) is a valid answer; other numeric steps require value > min. */
    allowZero?: boolean;
    prefix?: string;
    suffix?: string;
    format?: (n: number) => string;
  };
  answerKey:
    | "name"
    | "age"
    | "income"
    | "expenses"
    | "cash"
    | "debt"
    | "invest"
    | "risk";
}

const QUIZ_NAME_SUBTITLE = I18n.t("I'm Ed — your money person.");

const AGE_OPTIONS = [
  { id: "18-24", label: "18–24" },
  { id: "25-34", label: "25–34" },
  { id: "35-44", label: "35–44" },
  { id: "45-54", label: "45–54" },
  { id: "55+", label: "55+" },
];

const RISK_OPTIONS = [
  { id: "safe", label: I18n.t("Play it safe") },
  { id: "balanced", label: I18n.t("Balanced") },
  { id: "growth", label: I18n.t("Go for growth") },
];

export const QUIZ_STEP_DEFINITIONS: QuizStepDefinition[] = [
  {
    stepId: "quiz/name",
    figmaNodeIds: ["96121:8430", "96121:8432"],
    kind: "name",
    question: I18n.t("What should I call you?"),
    subtitle: QUIZ_NAME_SUBTITLE,
    answerKey: "name",
  },
  {
    stepId: "quiz/age",
    figmaNodeIds: ["96121:8433", "96121:8435"],
    kind: "choice",
    question: I18n.t("How old are you?"),
    options: AGE_OPTIONS,
    answerKey: "age",
  },
  {
    stepId: "quiz/income",
    figmaNodeIds: ["96121:8436", "96121:8437"],
    kind: "numeric",
    question: I18n.t("About how much do you earn?"),
    heroMessage: I18n.t(
      "Round numbers are fine — I'll sharpen them as I get to know you.",
    ),
    numeric: {
      min: 0,
      max: 500_000,
      step: 5_000,
      defaultValue: 120_000,
      prefix: "$",
      suffix: I18n.t("/ year"),
      format: (value) => (value === 0 ? "0k" : formatCompactCurrency(value)),
    },
    answerKey: "income",
  },
  {
    stepId: "quiz/expenses",
    figmaNodeIds: ["96121:8438"],
    kind: "numeric",
    question: I18n.t("And how much goes out a month?"),
    heroMessage: I18n.t("Got the top line. Now what goes out — roughly."),
    numeric: {
      min: 0,
      max: 20_000,
      step: 100,
      defaultValue: 3_200,
      prefix: "$",
      suffix: I18n.t("/ month"),
      format: formatMonthlyCurrency,
    },
    answerKey: "expenses",
  },
  {
    stepId: "quiz/cash",
    figmaNodeIds: ["96121:8439"],
    kind: "numeric",
    question: I18n.t("What's in the bank?"),
    heroMessage: I18n.t(
      "To get this right I just need one number: your everyday cash, sitting in checking and savings. Just drag.",
    ),
    numeric: {
      min: 0,
      max: 100_000,
      step: 1_000,
      defaultValue: 24_000,
      prefix: "$",
      suffix: I18n.t("total"),
      format: (value) => (value === 0 ? "0k" : formatCompactCurrency(value)),
    },
    answerKey: "cash",
  },
  {
    stepId: "quiz/debt",
    figmaNodeIds: ["96121:8440"],
    kind: "numeric",
    question: I18n.t("What do you owe?"),
    heroMessage: I18n.t(
      "Now the other side of the ledger — no judgment, it helps me plan.",
    ),
    numeric: {
      min: 0,
      max: 100_000,
      step: 1_000,
      defaultValue: 24_000,
      allowZero: true,
      prefix: "$",
      suffix: I18n.t("total"),
      format: (value) => (value === 0 ? "0k" : formatCompactCurrency(value)),
    },
    answerKey: "debt",
  },
  {
    stepId: "quiz/invest",
    figmaNodeIds: ["96121:8441"],
    kind: "numeric",
    question: I18n.t("How much have you got invested?"),
    heroMessage: I18n.t(
      "Last piece — what's already invested and working for you.",
    ),
    numeric: {
      min: 0,
      max: 200_000,
      step: 5_000,
      defaultValue: 40_000,
      prefix: "$",
      suffix: I18n.t("invested"),
      format: (value) => (value === 0 ? "0k" : formatCompactCurrency(value)),
    },
    answerKey: "invest",
  },
  {
    stepId: "quiz/risk",
    figmaNodeIds: ["96121:8442"],
    kind: "choice",
    question: I18n.t("What's your style with money?"),
    heroMessage: (name) =>
      `That's your whole money picture, ${name || "there"}. One last thing — about you, not your accounts.`,
    options: RISK_OPTIONS,
    answerKey: "risk",
  },
];

/** Resolve per-step Ed hero copy (string or name-aware function). */
export function resolveQuizHeroMessage(
  definition: Pick<QuizStepDefinition, "heroMessage">,
  displayName: string,
  fallback: (name: string) => string,
): string {
  if (typeof definition.heroMessage === "function") {
    return definition.heroMessage(displayName);
  }
  if (definition.heroMessage) {
    return definition.heroMessage;
  }
  return fallback(displayName);
}

function formatCompactCurrency(value: number): string {
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  }
  if (value >= 1_000) {
    return `${Math.round(value / 1_000)}k`;
  }
  return String(value);
}

/** Monthly expense slider — Figma 8438 uses comma grouping (e.g. 3,200). */
function formatMonthlyCurrency(value: number): string {
  return value.toLocaleString("en-US");
}

export interface GoalOptionDefinition {
  id: string;
  figmaNodeId: string;
  resultVariant: GoalResultVariant;
  icon: string;
  title: string;
  subtitle: string;
}

export const GOAL_OPTIONS: GoalOptionDefinition[] = [
  {
    id: "million",
    figmaNodeId: "96121:8443",
    resultVariant: "million",
    icon: "finance",
    title: I18n.t("When will I hit my first $1M?"),
    subtitle: I18n.t("The seven-figure milestone"),
  },
  {
    id: "home",
    figmaNodeId: "96121:8443",
    resultVariant: "home",
    icon: "home",
    title: I18n.t("What home could I afford?"),
    subtitle: I18n.t("And how to get the keys sooner"),
  },
  {
    id: "runway",
    figmaNodeId: "96121:8443",
    resultVariant: "runway",
    icon: "check-shield",
    title: I18n.t("How long could I last with no income?"),
    subtitle: I18n.t("The cushion that lets you walk away"),
  },
  {
    id: "idle-cash",
    figmaNodeId: "96121:8443",
    resultVariant: "idle-cash",
    icon: "dollar-bag",
    title: I18n.t("How much more could my idle cash earn?"),
    subtitle: I18n.t("Put lazy money to work"),
  },
  {
    id: "retirement",
    figmaNodeId: "96121:8443",
    resultVariant: "retirement",
    icon: "goal",
    title: I18n.t("When can I stop working for money?"),
    subtitle: I18n.t("When the paycheck stops mattering"),
  },
  {
    id: "debt-free",
    figmaNodeId: "96121:8443",
    resultVariant: "debt-free",
    icon: "receipt",
    title: I18n.t("When will I be debt-free?"),
    subtitle: I18n.t("Out from under it, for good"),
  },
];

export const RESULT_VARIANT_CONFIG: Record<
  GoalResultVariant,
  {
    figmaNodeId: string;
    bubbleLabel: string;
    bubbleQuestion: string;
    heroLine: string;
    heroValue: string;
    heroSubline: string;
    payoffTitle: string;
    payoffBody: string;
    cta: string;
  }
> = {
  million: {
    figmaNodeId: "96138:32114",
    bubbleLabel: I18n.t(
      "You’ve seen your Financial Fitness. Now — the thing you came in worried about.",
    ),
    bubbleQuestion: I18n.t("When will you hit your first $1M?"),
    heroLine: I18n.t("On your current pace, here's the year."),
    heroValue: "2048",
    heroSubline: I18n.t("that's $1,000,000 — around age 57."),
    payoffTitle: I18n.t("5 years sooner with Ed"),
    payoffBody: I18n.t("Ed runs the moves for you, automatically."),
    cta: I18n.t("Build my $1M plan"),
  },
  home: {
    figmaNodeId: "96138:32118",
    bubbleLabel: I18n.t(
      "You’ve seen your Financial Fitness. Now — the thing you came in worried about.",
    ),
    bubbleQuestion: I18n.t("What home could you afford?"),
    heroLine: I18n.t("On your numbers today, here's your ceiling."),
    heroValue: "$420k",
    heroSubline: "with your ~$84,000 deposit and current income.",
    payoffTitle: I18n.t("Ed extends budget by $80k for a larger home"),
    payoffBody: I18n.t(
      "Ed lifts your buying power without overstretching you.",
    ),
    cta: I18n.t("Build my home plan"),
  },
  runway: {
    figmaNodeId: "96138:32116",
    bubbleLabel: I18n.t(
      "You’ve seen your Financial Fitness. Now — the thing you came in worried about.",
    ),
    bubbleQuestion: I18n.t("How long could you last with no income?"),
    heroLine: I18n.t("If income stopped today, here's how long."),
    heroValue: "4 months",
    heroSubline: I18n.t("that's about $13,000 in the bank — gone by October."),
    payoffTitle: I18n.t("5 months longer with Ed"),
    payoffBody: I18n.t(
      "He frees up idle cash and builds your buffer, automatically.",
    ),
    cta: I18n.t("Build my runway plan"),
  },
  "idle-cash": {
    figmaNodeId: "96138:32120",
    bubbleLabel: I18n.t(
      "You’ve seen your Financial Fitness. Now — the thing you came in worried about.",
    ),
    bubbleQuestion: I18n.t("How much more could my idle cash earn?"),
    heroLine: I18n.t("Your cash is barely working today."),
    heroValue: "+$1,600/yr",
    heroSubline:
      "to start — and left to compound at ~4.5%, it grows far bigger.",
    payoffTitle: I18n.t("$34k more in 15 years, hands-off"),
    payoffBody: "That ~$1,600 a year quietly compounds while you sleep.",
    cta: I18n.t("Build my idlecash plan"),
  },
  retirement: {
    figmaNodeId: "96138:33167",
    bubbleLabel: I18n.t(
      "You’ve seen your Financial Fitness. Now — the thing you came in worried about.",
    ),
    bubbleQuestion: I18n.t("When can you stop working for money?"),
    heroLine: I18n.t("On your current pace, here's the age"),
    heroValue: "62",
    heroSubline: I18n.t("that's work-optional — about 14 years from now."),
    payoffTitle: I18n.t("4 years sooner with Ed"),
    payoffBody: I18n.t("Ed keeps your savings rate on track."),
    cta: I18n.t("Build my retirement plan"),
  },
  "debt-free": {
    figmaNodeId: "96138:33168",
    bubbleLabel: I18n.t(
      "You’ve seen your Financial Fitness. Now — the thing you came in worried about.",
    ),
    bubbleQuestion: I18n.t("When will you be debt-free?"),
    heroLine: I18n.t("On your current pace, you're clear by"),
    heroValue: "2031",
    heroSubline: I18n.t("that's your last payment — about 6 years out."),
    payoffTitle: I18n.t("2 years sooner with Ed"),
    payoffBody: I18n.t("Ed optimizes your payoff order for you."),
    cta: I18n.t("Build my debt-free plan"),
  },
};

export const PLAN_MOVES = [
  {
    number: 1,
    title: I18n.t("Put your idle cash to work"),
    tag: "+$1,600/yr",
    body: I18n.t("≈$40k sitting idle → a 4.5% position."),
    locked: false,
  },
  {
    number: 2,
    title: I18n.t("Lock a sharper mortgage rate"),
    tag: "+$40k home",
    body: I18n.t("Your refinance window is open right now."),
    locked: false,
  },
  {
    number: 3,
    title: I18n.t("Rebalance to reach $1M sooner"),
    tag: "~3 yrs sooner",
    body: I18n.t("Shift your mix so the milestone arrives years earlier."),
    locked: true,
  },
  {
    number: 4,
    title: I18n.t("A tax move you're missing"),
    tag: "+$2,300/yr",
    body: I18n.t("A simple shift that quietly hands you back money."),
    locked: true,
  },
  {
    number: 5,
    title: I18n.t("Build your safety cushion"),
    tag: I18n.t("Peace of mind"),
    body: I18n.t("Six months of breathing room, set on autopilot."),
    locked: true,
  },
] as const;
